import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { apiPost, apiPut, apiDelete } from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { SuppliersLoading } from '@/components/LoadingStates';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Phone, 
  Mail, 
  MapPin,
  Users,
  Eye,
  Loader2,
  Building2,
  Globe,
  Wallet,
  DollarSign,
  Flag
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 25, scale: 0.96 },
  show: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 100 } }
};

// Color palette matching the mockup
const colors = {
  cyan: '#22D3EE',
  orange: '#F97316',
  green: '#22C55E',
  blue: '#3B82F6',
  purple: '#A855F7',
  pink: '#EC4899',
  amber: '#F59E0B',
  red: '#EF4444'
};

// Avatar colors for different suppliers
const avatarColors = [
  'from-orange-500 to-amber-500',
  'from-cyan-500 to-blue-500',
  'from-emerald-500 to-green-500',
  'from-purple-500 to-pink-500',
  'from-blue-500 to-indigo-500',
  'from-pink-500 to-rose-500',
  'from-amber-500 to-yellow-500',
  'from-teal-500 to-cyan-500'
];

// Glow colors for cards
const glowColors = [
  { border: 'border-orange-500/40', shadow: 'shadow-orange-500/20', hover: 'hover:border-orange-500/60' },
  { border: 'border-cyan-500/40', shadow: 'shadow-cyan-500/20', hover: 'hover:border-cyan-500/60' },
  { border: 'border-emerald-500/40', shadow: 'shadow-emerald-500/20', hover: 'hover:border-emerald-500/60' },
  { border: 'border-purple-500/40', shadow: 'shadow-purple-500/20', hover: 'hover:border-purple-500/60' },
  { border: 'border-blue-500/40', shadow: 'shadow-blue-500/20', hover: 'hover:border-blue-500/60' },
  { border: 'border-pink-500/40', shadow: 'shadow-pink-500/20', hover: 'hover:border-pink-500/60' },
  { border: 'border-amber-500/40', shadow: 'shadow-amber-500/20', hover: 'hover:border-amber-500/60' },
  { border: 'border-teal-500/40', shadow: 'shadow-teal-500/20', hover: 'hover:border-teal-500/60' }
];

// Stat Card Component - Matching mockup style
const StatCard = ({ title, value, color, textColor, icon: Icon, prefix = '' }) => (
  <motion.div variants={itemVariants}>
    <Card className="relative bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden group hover:scale-[1.02] transition-transform">
      <div 
        className="absolute top-0 left-0 right-0 h-1"
        style={{ background: color }}
      />
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: `linear-gradient(135deg, ${color}10, transparent)` }}
      />
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] text-slate-500 uppercase tracking-wider font-medium mb-2">{title}</p>
            <p className="text-3xl font-black" style={{ color: textColor }}>
              {prefix}{typeof value === 'number' ? value.toLocaleString() : value}
            </p>
          </div>
          <div 
            className="p-3 rounded-xl"
            style={{ background: `${color}20` }}
          >
            <Icon className="h-6 w-6" style={{ color: color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

// Supplier Card Component - NEW MOCKUP DESIGN with colored glows
const SupplierCard = ({ supplier, cases, onEdit, onDelete, onView, onClick, index }) => {
  const supplierCases = cases.filter(c => c.supplierId === supplier.id || c.supplier_id === supplier.id);
  const workerCount = supplierCases.length;
  const currency = supplier.currency || 'AED';
  
  // Get color scheme based on index
  const colorIndex = index % avatarColors.length;
  const avatarColor = avatarColors[colorIndex];
  const glow = glowColors[colorIndex];
  
  // Get first letter for avatar
  const initial = supplier.name?.charAt(0)?.toUpperCase() || 'S';

  return (
    <motion.div 
      variants={itemVariants} 
      whileHover={{ y: -8, scale: 1.02 }} 
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      <Card 
        className={`relative bg-slate-900/80 backdrop-blur-xl border-2 ${glow.border} ${glow.hover} rounded-2xl overflow-hidden cursor-pointer group transition-all duration-300 shadow-xl ${glow.shadow}`}
        data-testid={`supplier-card-${supplier.id}`}
        onClick={onClick}
      >
        {/* Top gradient line with glow */}
        <div 
          className={`h-1.5 bg-gradient-to-r ${avatarColor}`}
        />
        
        <CardContent className="p-5">
          {/* Header Row */}
          <div className="flex items-start gap-4 mb-4">
            {/* Circular Avatar with glow effect */}
            <div className="relative">
              <div className={`absolute inset-0 bg-gradient-to-r ${avatarColor} rounded-full blur-lg opacity-40 group-hover:opacity-70 transition-opacity`} />
              <div className={`relative h-14 w-14 rounded-full bg-gradient-to-br ${avatarColor} flex items-center justify-center text-xl font-black text-white shadow-lg`}>
                {initial}
              </div>
            </div>
            
            {/* Name and Country */}
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-lg text-white truncate group-hover:text-white transition-colors">
                {supplier.name}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <Flag className="h-3.5 w-3.5 text-slate-500" />
                <span className="text-slate-400 text-sm">{supplier.country || 'Not specified'}</span>
              </div>
            </div>
            
            {/* Currency Badge */}
            <div className={`px-3 py-1.5 rounded-lg text-xs font-bold ${
              currency === 'USD' 
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
            }`}>
              {currency}
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-2.5 mb-4 bg-slate-950/40 rounded-xl p-3 border border-white/5">
            {supplier.contact && (
              <div className="flex items-center gap-2.5 text-slate-300 text-sm">
                <Phone className="h-4 w-4 text-slate-500" />
                <span dir="ltr">{supplier.contact}</span>
              </div>
            )}
            {supplier.phone && !supplier.contact && (
              <div className="flex items-center gap-2.5 text-slate-300 text-sm">
                <Phone className="h-4 w-4 text-slate-500" />
                <span dir="ltr">{supplier.phone}</span>
              </div>
            )}
            {supplier.email && (
              <div className="flex items-center gap-2.5 text-slate-300 text-sm">
                <Mail className="h-4 w-4 text-slate-500" />
                <span className="truncate">{supplier.email}</span>
              </div>
            )}
            {!supplier.contact && !supplier.phone && !supplier.email && (
              <div className="flex items-center gap-2.5 text-slate-500 text-sm">
                <Phone className="h-4 w-4" />
                <span>No contact info</span>
              </div>
            )}
          </div>

          {/* Workers Count Badge */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-cyan-400" />
              <span className="text-white font-semibold">{workerCount}</span>
              <span className="text-slate-500 text-sm">Workers</span>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              workerCount > 10 ? 'bg-emerald-500/20 text-emerald-400' : 
              workerCount > 0 ? 'bg-cyan-500/20 text-cyan-400' : 
              'bg-slate-700/50 text-slate-400'
            }`}>
              {workerCount > 10 ? 'Active' : workerCount > 0 ? 'Growing' : 'New'}
            </div>
          </div>

          {/* Action Buttons - Matching mockup style */}
          <div className="flex gap-2 pt-3 border-t border-white/5">
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); onView(supplier); }}
              className={`flex-1 bg-gradient-to-r ${avatarColor} hover:opacity-90 text-white font-semibold rounded-xl h-10`}
              data-testid={`view-supplier-${supplier.id}`}
            >
              <Eye className="h-4 w-4 mr-2" />
              View
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); onEdit(supplier); }}
              className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white rounded-xl h-10"
              data-testid={`edit-supplier-${supplier.id}`}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); onDelete(supplier); }}
              className="h-10 w-10 p-0 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-xl"
              data-testid={`delete-supplier-${supplier.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const SuppliersPage = () => {
  const navigate = useNavigate();
  const { suppliers, cases, fetchSuppliers, fetchCases, loading } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    contact: '',
    notes: '',
    currency: 'AED'
  });

  useEffect(() => {
    fetchSuppliers();
    fetchCases();
  }, [fetchSuppliers, fetchCases]);

  const filteredSuppliers = suppliers.filter(s => 
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.country?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openAddModal = () => {
    setEditingSupplier(null);
    setFormData({
      name: '',
      country: '',
      contact: '',
      notes: '',
      currency: 'AED'
    });
    setIsModalOpen(true);
  };

  const openEditModal = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name || '',
      country: supplier.country || '',
      contact: supplier.contact || '',
      notes: supplier.notes || '',
      currency: supplier.currency || 'AED'
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter supplier name');
      return;
    }

    setSaving(true);
    try {
      if (editingSupplier) {
        await apiPut(`/api/suppliers/${editingSupplier.id}`, formData);
        toast.success('Supplier updated successfully');
      } else {
        await apiPost('/api/suppliers', formData);
        toast.success('Supplier added successfully');
      }
      setIsModalOpen(false);
      fetchSuppliers(true);
    } catch (error) {
      console.error('Save error:', error);
      const errorMsg = typeof error.message === 'string' ? error.message : 'An error occurred';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (supplier) => {
    if (!window.confirm(`Are you sure you want to delete supplier "${supplier.name}"?`)) {
      return;
    }

    try {
      await apiDelete(`/api/suppliers/${supplier.id}`);
      toast.success('Supplier deleted successfully');
      fetchSuppliers(true);
    } catch (error) {
      console.error('Delete error:', error);
      const errorMsg = typeof error.message === 'string' ? error.message : 'Error deleting supplier';
      toast.error(errorMsg);
    }
  };

  const handleView = (supplier) => {
    navigate(`/suppliers/${supplier.id}`);
  };

  const isLoading = loading.suppliers || loading.cases;

  // Calculate stats
  const totalWorkers = cases.length;
  const usdSuppliers = suppliers.filter(s => s.currency === 'USD').length;
  const aedSuppliers = suppliers.filter(s => s.currency !== 'USD').length;

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      {/* Header - Matching mockup */}
      <motion.div 
        className="flex flex-col md:flex-row md:items-center justify-between gap-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-white">
            Suppliers
          </h1>
          <p className="text-slate-500 text-sm mt-1">Manage your supplier network</p>
        </div>
        
        <Button
          onClick={openAddModal}
          className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-lg shadow-orange-500/25 rounded-xl px-6 h-12 font-semibold"
          data-testid="add-supplier-btn"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add New Supplier
        </Button>
      </motion.div>

      {/* Search Bar - Matching mockup */}
      <motion.div 
        className="relative"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
        <Input
          placeholder="Search by name or country..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-12 h-12 bg-slate-900/70 backdrop-blur-xl border-white/10 text-white placeholder:text-slate-500 rounded-xl focus:border-orange-500/50 text-base"
          data-testid="search-suppliers-input"
        />
      </motion.div>

      {/* Stats Cards - Matching mockup exactly */}
      <motion.div 
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        <StatCard 
          title="Total Suppliers" 
          value={suppliers.length}
          color={colors.cyan}
          textColor="#ffffff"
          icon={Building2}
        />
        <StatCard 
          title="Total Workers" 
          value={totalWorkers}
          color={colors.cyan}
          textColor={colors.cyan}
          icon={Users}
        />
        <StatCard 
          title="USD Suppliers" 
          value={usdSuppliers}
          color={colors.green}
          textColor={colors.green}
          icon={DollarSign}
        />
        <StatCard 
          title="AED Suppliers" 
          value={aedSuppliers}
          color={colors.orange}
          textColor={colors.orange}
          icon={Wallet}
        />
      </motion.div>

      {/* Suppliers Grid - Matching mockup layout */}
      {isLoading && suppliers.length === 0 ? (
        <SuppliersLoading />
      ) : filteredSuppliers.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl">
            <CardContent className="p-16 text-center">
              <div className="h-20 w-20 mx-auto mb-6 rounded-2xl bg-slate-800/80 flex items-center justify-center">
                <Users className="h-10 w-10 text-slate-600" />
              </div>
              <p className="text-slate-400 text-lg mb-2">
                {searchTerm ? 'No search results' : 'No suppliers yet'}
              </p>
              <p className="text-slate-600 text-sm mb-6">
                {searchTerm ? 'Try a different search term' : 'Add your first supplier to get started'}
              </p>
              {!searchTerm && (
                <Button
                  onClick={openAddModal}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 rounded-xl px-6 h-11"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Supplier
                </Button>
              )}
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          {filteredSuppliers.map((supplier, index) => (
            <SupplierCard
              key={supplier.id}
              supplier={supplier}
              cases={cases}
              onEdit={openEditModal}
              onDelete={handleDelete}
              onView={handleView}
              onClick={() => handleView(supplier)}
              index={index}
            />
          ))}
        </motion.div>
      )}

      {/* Add/Edit Modal - Professional Design */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-slate-900 border border-white/10 text-white max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-3 text-xl">
              <div className={`p-2 rounded-lg ${editingSupplier ? 'bg-amber-500/20' : 'bg-orange-500/20'}`}>
                {editingSupplier ? (
                  <Edit className="h-5 w-5 text-amber-400" />
                ) : (
                  <Plus className="h-5 w-5 text-orange-400" />
                )}
              </div>
              {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Supplier Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter supplier name"
                className="bg-slate-950/50 border-white/10 text-white rounded-xl h-11 focus:border-orange-500/50"
                data-testid="supplier-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Country</Label>
              <Input
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                placeholder="e.g., Egypt, Philippines, Indonesia"
                className="bg-slate-950/50 border-white/10 text-white rounded-xl h-11 focus:border-orange-500/50"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Contact Info (Phone/Email)</Label>
              <Input
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                placeholder="+971... or email@example.com"
                dir="ltr"
                className="bg-slate-950/50 border-white/10 text-white rounded-xl h-11 focus:border-orange-500/50"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                className="bg-slate-950/50 border-white/10 text-white rounded-xl h-11 focus:border-orange-500/50"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Currency</Label>
              <Select
                value={formData.currency}
                onValueChange={(value) => setFormData({ ...formData, currency: value })}
              >
                <SelectTrigger className="bg-slate-950/50 border-white/10 text-white rounded-xl h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10 rounded-xl">
                  <SelectItem value="AED" className="text-white focus:bg-white/10">UAE Dirham (AED)</SelectItem>
                  <SelectItem value="USD" className="text-white focus:bg-white/10">US Dollar (USD)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              className="border-white/10 text-slate-300 hover:bg-white/5 rounded-xl h-11 px-6"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 rounded-xl h-11 px-6"
              data-testid="save-supplier-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  {editingSupplier ? 'Update Supplier' : 'Add Supplier'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuppliersPage;
